import React, { useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { LabelWithInfo } from '../../components/InfoTooltip';
import PeriodFilterBar from '../../components/Management/PeriodFilterBar';
import HealthBadge, { formatCurrency, SectionTitle } from '../../components/Management/HealthBadge';
import { analyticsAPI } from '../../services/analytics';
import { managementHelp } from '../../content/managementHelp';
import { useAuth } from '../../contexts/AuthContext';
import {
  leadCohortLine1,
  leadCohortLine2,
  leadCohortDemoTrialLine,
  leadCohortActivityLabel,
  pulseLeadCohortSubtitle,
  pulseActivityLabel,
} from '../../utils/leadCohortMetrics';
import {
  trainerRankingLine1,
  trainerRankingLine2,
  trainerRankingDemoTrialLine,
  trainerRankingClosedLabel,
} from '../../utils/trainerRankingMetrics';
import {
  dimensionFromSourceRow,
  dimensionFromCountryRow,
  dimensionFromSalesRow,
} from '../../utils/leadListDeepLinks';

function PulseCard({ title, help, children, health }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <LabelWithInfo label={<Typography variant="subtitle1">{title}</Typography>} title={help.title} body={help.body} />
          {health && <HealthBadge healthStatus={health.healthStatus} healthLabel={health.healthLabel} />}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function ScorecardRow({ name, line1, line2, line3, line4, health }) {
  return (
    <Box
      sx={{
        p: 2,
        mb: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 1,
      }}
    >
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          {line1}
        </Typography>
        {line2 && (
          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            {line2}
          </Typography>
        )}
        {line3 && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {line3}
          </Typography>
        )}
        {line4 && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {line4}
          </Typography>
        )}
      </Box>
      {health && <HealthBadge healthStatus={health.healthStatus} healthLabel={health.healthLabel} />}
    </Box>
  );
}

function TrainerPerformanceSection({
  byTrainer,
  reportPeriod,
  period,
  customFrom,
  customTo,
  navigate,
}) {
  return (
    <>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, mt: 1 }}>
        Who is performing
      </Typography>
      <Card sx={{ mb: 4, mt: 0.5 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Tabs value={0}>
              <Tab
                label={
                  <LabelWithInfo
                    label="Trainers"
                    title={managementHelp.trainers.title}
                    body={managementHelp.trainers.body}
                  />
                }
              />
            </Tabs>
            <Button
              size="small"
              onClick={() => {
                const qs = new URLSearchParams({ type: 'trainers', period });
                if (period === 'custom' && customFrom && customTo) {
                  qs.set('from', customFrom);
                  qs.set('to', customTo);
                }
                navigate(`/management/rankings?${qs.toString()}`);
              }}
            >
              View all
            </Button>
          </Box>
          {(byTrainer || []).map((row) => {
            const linkCtx = { period: reportPeriod };
            return (
              <ScorecardRow
                key={row.teacherId}
                name={row.name}
                line1={trainerRankingLine1(row, linkCtx)}
                line2={trainerRankingLine2(row)}
                line3={trainerRankingDemoTrialLine(row, linkCtx)}
                line4={
                  <LabelWithInfo
                    label={trainerRankingClosedLabel(row, linkCtx)}
                    title={managementHelp.trainers.closedThisMonth.title}
                    body={managementHelp.trainers.closedThisMonth.body}
                  />
                }
                health={row}
              />
            );
          })}
          {(byTrainer || []).length === 0 && (
            <Typography color="text.secondary">No trainer data for this period.</Typography>
          )}
        </CardContent>
      </Card>
    </>
  );
}

const ManagementDashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, isTeacherAdmin } = useAuth();
  // teacher_admin (non-platform-admin): trainers slice only — never call full dashboard API
  const trainersOnly = isTeacherAdmin() && !isAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const period = searchParams.get('period') || 'this_month';
  const customFrom = searchParams.get('from') || '';
  const customTo = searchParams.get('to') || '';
  const [draftFrom, setDraftFrom] = useState(customFrom);
  const [draftTo, setDraftTo] = useState(customTo);
  const [channelTab, setChannelTab] = useState(0);
  const [peopleTab, setPeopleTab] = useState(0);
  const [pendingCustom, setPendingCustom] = useState(period === 'custom');

  const queryParams = useMemo(() => {
    const p = { period };
    if (period === 'custom' && customFrom && customTo) {
      p.from = customFrom;
      p.to = customTo;
    }
    return p;
  }, [period, customFrom, customTo]);

  const queryEnabled = period !== 'custom' || Boolean(customFrom && customTo);

  const { data: res, isLoading, error, isFetching } = useQuery(
    [trainersOnly ? 'managementByTrainer' : 'managementDashboard', queryParams],
    () => (trainersOnly ? analyticsAPI.getByTrainer(queryParams) : analyticsAPI.getDashboard(queryParams)),
    { staleTime: 60000, keepPreviousData: true, enabled: queryEnabled }
  );

  const data = useMemo(() => {
    if (trainersOnly) {
      const payload = res?.data?.data;
      return {
        period: payload?.period,
        byTrainer: (payload?.rows || []).slice(0, 10),
      };
    }
    return res?.data?.data;
  }, [trainersOnly, res]);

  const setPeriod = useCallback(
    (nextPeriod, from, to) => {
      const next = new URLSearchParams();
      next.set('period', nextPeriod);
      if (nextPeriod === 'custom' && from && to) {
        next.set('from', from);
        next.set('to', to);
      }
      setSearchParams(next);
    },
    [setSearchParams]
  );

  const periodLabel = data?.period
    ? `${data.period.from} → ${data.period.to} (IST)`
    : '';

  const chartData = useMemo(
    () => (data?.revenueTrend?.points || []).map((p) => ({ ...p, name: p.label })),
    [data]
  );

  const hasLoaded = trainersOnly
    ? Boolean(data?.period || (Array.isArray(data?.byTrainer) && res))
    : Boolean(data);

  if (isLoading && !hasLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error.response?.data?.message || error.message || 'Failed to load dashboard'}
      </Alert>
    );
  }

  const periodFilter = (
    <PeriodFilterBar
      period={pendingCustom ? 'custom' : period}
      customFrom={draftFrom}
      customTo={draftTo}
      periodLabel={periodLabel}
      onPeriodChange={(p) => {
        if (p === 'custom') {
          setPendingCustom(true);
          return;
        }
        setPendingCustom(false);
        setPeriod(p);
      }}
      onCustomFromChange={setDraftFrom}
      onCustomToChange={setDraftTo}
      onApplyCustom={() => {
        setPendingCustom(false);
        setPeriod('custom', draftFrom, draftTo);
      }}
    />
  );

  if (trainersOnly) {
    return (
      <Box>
        {periodFilter}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LabelWithInfo
            label={
              <Typography variant="h5" component="h1" fontWeight={700}>
                Trainer performance
              </Typography>
            }
            title={managementHelp.trainers.title}
            body={managementHelp.trainers.body}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Metrics below use the reporting period selected above.
        </Typography>
        {isFetching && <LinearProgress sx={{ mb: 2 }} />}
        {period === 'custom' && !queryEnabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a from and to date, then click Apply dates.
          </Alert>
        )}
        <TrainerPerformanceSection
          byTrainer={data?.byTrainer}
          reportPeriod={data?.period}
          period={period}
          customFrom={customFrom}
          customTo={customTo}
          navigate={navigate}
        />
      </Box>
    );
  }

  const pulse = data?.pulse;
  const funnelCohort = data?.funnel?.cohortStages || data?.funnel?.stages || [];
  const funnelActivity = data?.funnel?.activityStages || [];
  const maxFunnel = Math.max(...funnelCohort.map((s) => s.count), 1);
  const maxActivityFunnel = Math.max(...funnelActivity.map((s) => s.count), 1);
  const ageing = data?.followUpAgeing;
  const reportPeriod = data?.period;

  return (
    <Box>
      {periodFilter}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LabelWithInfo
          label={
            <Typography variant="h5" component="h1" fontWeight={700}>
              Sales & Enrollment
            </Typography>
          }
          title={managementHelp.page.title}
          body={managementHelp.page.body}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Metrics below use the reporting period selected above, except where noted.
      </Typography>

      {isFetching && <LinearProgress sx={{ mb: 2 }} />}

      {period === 'custom' && !queryEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a from and to date, then click Apply dates.
        </Alert>
      )}

      {/* Business pulse */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
        Business pulse
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4, mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <PulseCard title="Lead → Enrollment" help={managementHelp.pulse.leadToEnrollment} health={pulse?.leadToEnrollment}>
            <Typography variant="h4" fontWeight="bold">
              {pulse?.leadToEnrollment?.pct ?? 0}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pulseLeadCohortSubtitle(pulse)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              <LabelWithInfo
                label={pulseActivityLabel(pulse)}
                title={managementHelp.pulse.leadToEnrollment.activity.title}
                body={managementHelp.pulse.leadToEnrollment.activity.body}
              />
            </Typography>
          </PulseCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <PulseCard title="Revenue" help={managementHelp.pulse.revenue} health={pulse?.revenue}>
            <Typography variant="h4" fontWeight="bold">
              {formatCurrency(pulse?.revenue?.amount, pulse?.revenue?.currency)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pulse?.revenue?.financeAvailable === false
                ? 'Finance data unavailable'
                : `vs ${formatCurrency(pulse?.revenue?.priorAmount, pulse?.revenue?.currency)} prior period`}
            </Typography>
          </PulseCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <PulseCard title="Enrollments" help={managementHelp.pulse.enrollments} health={pulse?.enrollments}>
            <Typography variant="h4" fontWeight="bold">
              {pulse?.enrollments?.count ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              vs {pulse?.enrollments?.priorCount ?? 0} prior period
            </Typography>
          </PulseCard>
        </Grid>
      </Grid>

      {/* Pipeline */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, mt: 1 }}>
        Pipeline
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4, mt: 0.5 }}>
        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent>
              <SectionTitle
                title="Funnel"
                help={
                  <LabelWithInfo
                    label=""
                    title={managementHelp.funnel.section.title}
                    body={managementHelp.funnel.section.body}
                  />
                }
              />
              {data?.attendanceEstimated && (
                <Alert severity="info" sx={{ mb: 2, py: 0 }}>
                  Attendance estimated until per-lead attendance is recorded at demo close.
                </Alert>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <LabelWithInfo
                  label="Cohort funnel"
                  title={managementHelp.funnel.cohort.title}
                  body={managementHelp.funnel.cohort.body}
                />
              </Typography>
              {funnelCohort.map((stage) => (
                <Box key={stage.key} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{stage.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stage.count}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={(stage.count / maxFunnel) * 100} sx={{ height: 8, borderRadius: 1 }} />
                </Box>
              ))}
              {funnelActivity.length > 0 ? (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    <LabelWithInfo
                      label="Activity in period"
                      title={managementHelp.funnel.activity.title}
                      body={managementHelp.funnel.activity.body}
                    />
                  </Typography>
                  {funnelActivity.map((stage) => (
                    <Box key={`activity-${stage.key}`} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {stage.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {stage.count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(stage.count / maxActivityFunnel) * 100}
                        sx={{ height: 6, borderRadius: 1, opacity: 0.75 }}
                      />
                    </Box>
                  ))}
                </>
              ) : null}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <SectionTitle
                  title="Stuck after demo"
                  help={
                    <LabelWithInfo
                      label=""
                      title={managementHelp.ageing.section.title}
                      body={managementHelp.ageing.section.body}
                    />
                  }
                />
                {ageing && <HealthBadge healthStatus={ageing.healthStatus} healthLabel={ageing.healthLabel} />}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current pipeline as of today — not filtered by the date range above.
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                {ageing?.stuckTotal ?? 0} leads waiting
              </Typography>
              <Grid container spacing={1}>
                {(ageing?.buckets || []).map((b) => (
                  <Grid item xs={6} sm={3} key={b.key}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/management/stuck-leads?bucket=${b.key}`)}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(`/management/stuck-leads?bucket=${b.key}`)}
                      sx={{
                        p: 1.5,
                        border: 2,
                        borderColor:
                          b.healthStatus === 'poor'
                            ? 'error.main'
                            : b.healthStatus === 'needs_attention'
                              ? 'warning.main'
                              : 'success.light',
                        borderRadius: 1,
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="caption" display="block">
                        {b.label}
                      </Typography>
                      <Typography variant="h6">{b.count}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Button sx={{ mt: 2 }} size="small" onClick={() => navigate('/management/stuck-leads')}>
                View stuck leads list
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Channels */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, mt: 1 }}>
        What is working
      </Typography>
      <Card sx={{ mb: 4, mt: 0.5 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Tabs value={channelTab} onChange={(_, v) => setChannelTab(v)}>
              <Tab label={<LabelWithInfo label="Lead sources" title={managementHelp.sources.title} body={managementHelp.sources.body} />} />
              <Tab label={<LabelWithInfo label="Countries" title={managementHelp.countries.title} body={managementHelp.countries.body} />} />
            </Tabs>
            <Button
              size="small"
              onClick={() => {
                const type = channelTab === 0 ? 'sources' : 'countries';
                const qs = new URLSearchParams({ type, period });
                if (period === 'custom' && customFrom && customTo) {
                  qs.set('from', customFrom);
                  qs.set('to', customTo);
                }
                navigate(`/management/rankings?${qs.toString()}`);
              }}
            >
              View all
            </Button>
          </Box>
          {channelTab === 0 &&
            (data?.bySource || []).map((row) => {
              const dim = dimensionFromSourceRow(row);
              const linkCtx = { period: reportPeriod, dimension: dim };
              return (
                <ScorecardRow
                  key={row.sourceGroup}
                  name={row.sourceGroup}
                  line1={leadCohortLine1(row, linkCtx)}
                  line2={leadCohortLine2(row)}
                  line3={leadCohortDemoTrialLine(row, linkCtx)}
                  line4={
                    <LabelWithInfo
                      label={leadCohortActivityLabel(row, linkCtx)}
                      title={managementHelp.sources.activity.title}
                      body={managementHelp.sources.activity.body}
                    />
                  }
                  health={row}
                />
              );
            })}
          {channelTab === 1 &&
            (data?.byCountry || []).map((row) => {
              const dim = dimensionFromCountryRow(row);
              const linkCtx = { period: reportPeriod, dimension: dim };
              return (
                <ScorecardRow
                  key={row.country}
                  name={row.country}
                  line1={leadCohortLine1(row, linkCtx)}
                  line2={leadCohortLine2(row)}
                  line3={leadCohortDemoTrialLine(row, linkCtx)}
                  line4={
                    <LabelWithInfo
                      label={leadCohortActivityLabel(row, linkCtx)}
                      title={managementHelp.countries.activity.title}
                      body={managementHelp.countries.activity.body}
                    />
                  }
                  health={row}
                />
              );
            })}
        </CardContent>
      </Card>

      {/* People */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, mt: 1 }}>
        Who is performing
      </Typography>
      <Card sx={{ mb: 4, mt: 0.5 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Tabs value={peopleTab} onChange={(_, v) => setPeopleTab(v)}>
              <Tab label={<LabelWithInfo label="Sales" title={managementHelp.sales.title} body={managementHelp.sales.body} />} />
              <Tab label={<LabelWithInfo label="Trainers" title={managementHelp.trainers.title} body={managementHelp.trainers.body} />} />
            </Tabs>
            <Button
              size="small"
              onClick={() => {
                const type = peopleTab === 0 ? 'sales' : 'trainers';
                const qs = new URLSearchParams({ type, period });
                if (period === 'custom' && customFrom && customTo) {
                  qs.set('from', customFrom);
                  qs.set('to', customTo);
                }
                navigate(`/management/rankings?${qs.toString()}`);
              }}
            >
              View all
            </Button>
          </Box>
          {peopleTab === 0 &&
            (data?.bySales || []).map((row) => {
              const dim = dimensionFromSalesRow(row);
              const linkCtx = { period: reportPeriod, dimension: dim };
              return (
                <ScorecardRow
                  key={row.userId || row.name}
                  name={row.name}
                  line1={leadCohortLine1(row, linkCtx)}
                  line2={leadCohortLine2(row)}
                  line3={leadCohortDemoTrialLine(row, linkCtx)}
                  line4={
                    <LabelWithInfo
                      label={leadCohortActivityLabel(row, linkCtx)}
                      title={managementHelp.sales.activity.title}
                      body={managementHelp.sales.activity.body}
                    />
                  }
                  health={row}
                />
              );
            })}
          {peopleTab === 1 &&
            (data?.byTrainer || []).map((row) => {
              const linkCtx = { period: reportPeriod };
              return (
                <ScorecardRow
                  key={row.teacherId}
                  name={row.name}
                  line1={trainerRankingLine1(row, linkCtx)}
                  line2={trainerRankingLine2(row)}
                  line3={trainerRankingDemoTrialLine(row, linkCtx)}
                  line4={
                    <LabelWithInfo
                      label={trainerRankingClosedLabel(row, linkCtx)}
                      title={managementHelp.trainers.closedThisMonth.title}
                      body={managementHelp.trainers.closedThisMonth.body}
                    />
                  }
                  health={row}
                />
              );
            })}
        </CardContent>
      </Card>

      {/* Trend */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, mt: 1 }}>
        Revenue trend
      </Typography>
      <Card sx={{ mb: 2, mt: 0.5 }}>
        <CardContent>
          <SectionTitle
            title="Revenue & enrollments"
            help={<LabelWithInfo label="" title={managementHelp.trend.title} body={managementHelp.trend.body} />}
          />
          {chartData.length === 0 ? (
            <Typography color="text.secondary">No trend data for this period.</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#1976d2" />
                <Line yAxisId="right" type="monotone" dataKey="enrollments" name="Enrollments" stroke="#2e7d32" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManagementDashboard;
